// Create-pipeline form. Intentionally minimal — just the name. Everything
// else (blueprints, knowledge sources, operational sources, KG settings,
// post-run automation) is configured on the detail page after creation,
// where it has more room to breathe.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, type CSSProperties } from 'react';

import { BackButton } from '../../../components/common/BackButton';
import { PageHeader } from '../../../components/common/PageHeader';
import { routes } from '../../../lib/constants';
import { useMetaphysicsPipelines } from '../../../lib/metaphysics-pipelines';
import { guardSuperuser } from '../index';

export const Route = createFileRoute('/internal/pipelines/new')({
  beforeLoad: () => guardSuperuser(),
  component: NewPipelinePage,
});

const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };

function NewPipelinePage() {
  const mp = useMetaphysicsPipelines();
  const qc = useQueryClient();
  const nav = useNavigate();

  const [name, setName] = useState('');

  const create = useMutation({
    mutationFn: () =>
      mp.createPipeline({
        name,
        blueprints: [],
        knowledgeSources: [],
        operationalSources: [],
      }),
    onSuccess: async (p) => {
      try {
        await qc.invalidateQueries({ queryKey: ['mp', 'pipelines'] });
      } catch (err) {
        console.error(err);
      }
      try {
        await nav({ to: routes.internalPipelineDetail(p.id) });
      } catch (err) {
        console.error(err);
      }
    },
  });

  const canSubmit = name.trim().length > 0 && !create.isPending;

  return (
    <>
      <PageHeader
        title="Create pipeline"
        actions={<BackButton to={routes.INTERNAL_PIPELINES} label="Pipelines" />}
      />
      <div className="card" style={{ maxWidth: 560 }}>
        <div className="card-body">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) create.mutate();
            }}
            style={stack3}
          >
            <label className="field">
              <span className="field-label">Name</span>
              <input
                className="input"
                required={true}
                placeholder="my-pipeline"
                autoFocus={true}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>

            {create.error ? (
              <div className="alert alert--danger">{(create.error as Error).message}</div>
            ) : null}

            <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
              <Link to={routes.INTERNAL_PIPELINES} className="btn btn--ghost btn--sm">
                Cancel
              </Link>
              <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
                {create.isPending ? 'Creating…' : 'Create pipeline'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
